import "./index.css";
import "@dexibit/styles/themes.css";
import "@dexibit/styles/fonts.css";

import { StrictMode } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { AsyncProviderConfig, asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import { SnackbarProvider } from "@dexibit/core";
import { DexibitThemeProvider } from "@dexibit/styles";

import App from "./App";
import { AppI18nProvider } from "./components/AppI18nProvider";
import ErrorFallback from "./components/ErrorFallback";
import { NavigationHelmet } from "./NavigationHelmet";
import { configService } from "./store/services/config";
import { store } from "./store/store";
import getDexibitTheme from "./utils/dexibitTheme";
import { setEnv } from "./utils/getEnv";
import logger from "./utils/logger";
import initDatadog from "./utils/logger/datadog";
import tokenManager from "./utils/tokenManager";
import toUserContext from "./utils/toUserContext";

import type { ConfigDTO } from "../common/types/ConfigDTO";

// Vite emits `vite:preloadError` when a code-split chunk fails to load, which
// on prod almost always means a deploy happened while this tab was open and
// the hashed chunk no longer exists. A reload picks up the new index.html and
// its new chunk references. The session-storage debounce stops us looping if
// the reload itself lands on the same broken state.
const STALE_CHUNK_RELOAD_KEY = "nav-vite-preload-reload-at";
const STALE_CHUNK_RELOAD_DEBOUNCE_MS = 30_000;

window.addEventListener("vite:preloadError", (event) => {
  const last = Number(window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) ?? 0);
  if (Date.now() - last < STALE_CHUNK_RELOAD_DEBOUNCE_MS) {
    return;
  }
  window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
  const payload = (event as Event & { payload?: Error }).payload;
  logger.warn("Reloading after vite:preloadError", undefined, payload);
  event.preventDefault();
  window.location.reload();
});

const LD_INIT_TIMEOUT_SECONDS = 5;

void (async () => {
  // Fetch runtime config first so Datadog is initialised with the real env
  // before React mounts. Dispatching via RTK Query populates its cache, so
  // useGetConfigQuery() hooks in the React tree don't re-fetch on mount.
  let config: ConfigDTO | undefined;
  try {
    config = await store.dispatch(configService.endpoints.getConfig.initiate()).unwrap();
    setEnv(config.environ);
    initDatadog();
  } catch (err) {
    console.error("Failed to fetch bootstrap config", err);
  }

  if (!config?.launchDarklyClientSideId) {
    throw new Error("LaunchDarkly client-side ID not found in runtime config");
  }

  const userData = tokenManager.getUserData();

  if (userData) {
    const userDataToReport = {
      id: String(userData.id),
      email: userData.email,
      accountId: userData.accountId,
      roles: userData.roles,
    };
    datadogRum.setUser(userDataToReport);
    datadogLogs.setUser(userDataToReport);
  }

  const container = document.getElementById("root");

  if (!container) {
    throw new Error("Could not find root container");
  }

  const root = createRoot(container);

  const context = toUserContext(userData);

  const launchDarklyConfig: AsyncProviderConfig = {
    clientSideID: config.launchDarklyClientSideId,
    // Without a timeout a hung LaunchDarkly blocks the first render indefinitely;
    // on expiry the app renders and flags resolve (or not) in the background.
    timeout: LD_INIT_TIMEOUT_SECONDS,
    options: {
      // Use our logger so it gets to our puppy DataDog as well
      logger,
    },
    context,
  };

  const LDProvider = await asyncWithLDProvider(launchDarklyConfig);

  root.render(
    <StrictMode>
      <HelmetProvider>
        <DexibitThemeProvider themeId={getDexibitTheme()}>
          <SnackbarProvider>
            <Provider store={store}>
              <AppI18nProvider>
                <NavigationHelmet />
                <BrowserRouter>
                  <LDProvider>
                    <ErrorBoundary
                      FallbackComponent={ErrorFallback}
                      onError={(error: Error) => logger.error("Global error", error)}
                    >
                      <App />
                    </ErrorBoundary>
                  </LDProvider>
                </BrowserRouter>
              </AppI18nProvider>
            </Provider>
          </SnackbarProvider>
        </DexibitThemeProvider>
      </HelmetProvider>
    </StrictMode>,
  );
})();
